import { useState } from "react";
import { X, Key, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function UnlockModal({ open, onClose, onUnlock }) {
  const [email, setEmail] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const ok = await onUnlock(email.trim().toLowerCase(), licenseKey.trim().toUpperCase());
      if (ok) {
        toast.success("Pro unlocked. Welcome to the lab.");
        onClose();
      } else {
        toast.error("That email + key combination doesn't match any license.");
      }
    } catch (err) {
      toast.error("Verification failed. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="unlock-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md border border-border bg-card p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          data-testid="unlock-modal-close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center hover:bg-secondary rounded-sm"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 grid place-items-center bg-primary text-primary-foreground rounded-sm">
            <Key size={16} />
          </div>
          <div>
            <div className="overline">Aviary Lab Pro</div>
            <h2 className="text-xl font-bold tracking-tight">Unlock with license key</h2>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="overline mb-1">Purchase email</div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="unlock-input-email"
              className="w-full border border-border bg-background px-3 py-2 text-sm font-mono rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">License key</div>
            <input
              type="text"
              required
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="BUDGIE-XXXX-XXXX-XXXX"
              data-testid="unlock-input-key"
              className="w-full border border-border bg-background px-3 py-2 text-sm font-mono tracking-wider rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="unlock-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium disabled:opacity-60"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Key size={16} />}
            {submitting ? "Verifying…" : "Unlock Pro"}
          </button>
        </form>

        <p className="mt-4 text-xs text-muted-foreground font-mono">
          // Lost your key? Re-send it from your purchase email or contact the lab.
        </p>
      </div>
    </div>
  );
}
